﻿import { HangoutState, HANGOUT_EVENTS } from '../../js/hangout/state/HangoutState.js';
import { eventBus } from '../events/index.js';
import { storageManager } from '../storage/index.js';
import { storage } from '../../js/utils/storage.js';
import { resolvePreferredLocalMessages } from '../../js/hangout/shared/history-resolution.js';
import {
  normalizeTimestamp,
  sortMessagesByTimestamp,
  findLatestMessage
} from '../utils/timestampUtils.js';
import {
  extractCurrentStateMetadata,
  validateStateDiff
} from '../utils/stateDiffMapper.js';

export class ConversationRestorationService {
  constructor() {
    this.hangoutState = null;
    this.isRestoring = false;
    this.authToken = null;
  }

  async initialize(hangoutState) {
    this.hangoutState = hangoutState || HangoutState.getInstance();
    await this._initializeAuthToken();
  }

  async _initializeAuthToken() {
    try {
      this.authToken = (typeof window !== 'undefined' && window.tokenManager)
        ? await window.tokenManager.getValidAccessToken()
        : null;
    } catch (error) {
      console.warn('Auth token init failed:', error);
    }
  }

  async restoreFromRefresh(options = {}) {
    if (this.isRestoring) return { success: false, reason: 'restoration_in_progress' };

    try {
      this.isRestoring = true;
      const hangoutConfig = await storageManager.getItem('currentHangoutConfig');

      // Don't try to restore if this is a new conversation or no conversationId
      if (!hangoutConfig?.conversationId || hangoutConfig.conversationId === null || hangoutConfig.isNewConversation) {
        return { success: false, reason: 'no_existing_conversation' };
      }

      const conversationId = hangoutConfig.conversationId;
      const { messages: localMessages } = resolvePreferredLocalMessages(conversationId);

      if (localMessages.length === 0) {
        return { success: false, reason: 'no_local_messages' };
      }

      const sortedMessages = sortMessagesByTimestamp(localMessages);
      const latestMessage = findLatestMessage(sortedMessages);

      if (!latestMessage) {
        return { success: false, reason: 'invalid_messages' };
      }

      const restorationData = {
        conversationId,
        messages: sortedMessages,
        latestMessage,
        sourceType: 'page_refresh',
        config: hangoutConfig
      };

      const applicationResult = await this._applyRestoredState({}, restorationData);

      return {
        success: true,
        conversationId,
        messagesRestored: sortedMessages.length,
        latestMessage,
        sourceType: 'page_refresh',
        ...applicationResult
      };

    } catch (error) {
      return { success: false, reason: 'error', error: error.message };
    } finally {
      this.isRestoring = false;
    }
  }

  async restoreFromHistory(conversationId, options = {}) {
    if (this.isRestoring) return { success: false, reason: 'restoration_in_progress' };

    try {
      this.isRestoring = true;
      const conversationData = await this._fetchCompleteConversation(conversationId);

      if (!conversationData.success) return conversationData;

      const scenarioData = await this._getScenarioData(conversationData.scenarioId);
      const latestMessage = this._findLatestMessageByTimestamp(conversationData.messages);

      if (!latestMessage) {
        return { success: false, reason: 'no_latest_message' };
      }

      const pathMessages = await this._buildPathToMessage(conversationData.messages, latestMessage);
      const reconstructedState = await this._reconstructStateFromPath(pathMessages, scenarioData);

      const restorationData = {
        conversationId,
        messages: conversationData.messages,
        latestMessage,
        scenarioData,
        pathMessages,
        sourceType: 'history_restore'
      };

      const applicationResult = await this._applyRestoredState(reconstructedState, restorationData);

      return {
        success: true,
        conversationId,
        messagesRestored: conversationData.messages.length,
        latestMessage,
        scenarioData,
        sourceType: 'history_restore',
        ...applicationResult
      };

    } catch (error) {
      return { success: false, reason: 'error', error: error.message };
    } finally {
      this.isRestoring = false;
    }
  }

  async _fetchCompleteConversation(conversationId) {
    try {
      let messages = [];
      let scenarioId = null;
      const { messages: preferredMessages, source } = resolvePreferredLocalMessages(conversationId);
      if (preferredMessages.length > 0) {
        messages = preferredMessages;
        const metadata = storage.getConversationMetadata(conversationId);
        scenarioId = metadata?.scenarioId || null;
        console.log(`ConversationRestorationService: Using ${source} with`, messages.length, 'messages');
      }

      if (messages.length === 0) {
        return { success: false, reason: 'no_messages_found' };
      }

      this._conversationCharacterName = null;
      this._conversationAllCharacters = null;

      if (!scenarioId) {
        const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
        scenarioId = hangoutConfig?.scenarioId || null;
      }

      const normalizedMessages = messages.map(msg => {
        const timestamps = normalizeTimestamp(msg.createdAt);
        const normalized = {
          ...msg,
          createdAt: timestamps.js,
          path: msg.path ? String(msg.path) : String(timestamps.js)
        };
        // Ensure characterId is string
        if (normalized.characterId !== undefined && normalized.characterId !== null) {
          normalized.characterId = String(normalized.characterId);
        }
        // Keep path as-is (message sequence string)
        if (normalized.path !== undefined && normalized.path !== null) {
          normalized.path = String(normalized.path);
        }
        return normalized;
      });

      return {
        success: true,
        messages: normalizedMessages,
        scenarioId: scenarioId || 'unknown'
      };

    } catch (error) {
      return { success: false, reason: 'fetch_error', error: error.message };
    }
  }

  async _getScenarioData(scenarioId) {
    try {
      if (!scenarioId || scenarioId === 'unknown') {
        return { id: 'unknown', description: 'Unknown scenario', location: '0' };
      }

      const scenarios = this.hangoutState.getScenarios();
      const scenario = scenarios.find(s => String(s.id) === String(scenarioId));

      return scenario || {
        id: scenarioId,
        description: `Scenario ${scenarioId}`,
        location: '0'
      };

    } catch (error) {
      return { id: 'unknown', description: 'Error loading scenario', location: '0' };
    }
  }

  _findLatestMessageByTimestamp(messages) {
    if (!messages?.length) return null;
    return findLatestMessage(messages);
  }

  async _buildPathToMessage(messages, targetMessage) {
    try {
      const sortedMessages = sortMessagesByTimestamp(messages);
      const targetIndex = sortedMessages.findIndex(msg =>
        String(msg.path || '') === String(targetMessage.path || '')
      );

      return targetIndex === -1 ? messages : sortedMessages.slice(0, targetIndex + 1);
    } catch (error) {
      return messages;
    }
  }

  async _reconstructStateFromPath(pathMessages, scenarioData) {
    try {
      let currentState = {
        locationId: scenarioData?.locId || scenarioData?.loc || scenarioData?.background_id || 0,
        timeOfDay: 'night',
        activeCharacters: [],
        goalChanges: {}
      };

      const activeCharacterMap = new Map(
        currentState.activeCharacters.map((character) => [String(character.id), character])
      );
      pathMessages.forEach(message => {
        if (message.role === 'assistant' && message.characterId) {
          const id = String(message.characterId);
          const prev = activeCharacterMap.get(id) || { id, goal: null };
          activeCharacterMap.set(id, {
            id,
            outfitId: String(message.outfitId ?? prev.outfitId ?? 0),
            actionId: message.actionId ?? prev.actionId ?? null,
            actionName: message.actionName || prev.actionName || null,
            name: message.characterName || prev.name,
            goal: prev.goal
          });
        }
      });
      currentState.activeCharacters = Array.from(activeCharacterMap.values());

      return currentState;

    } catch (error) {
      return {
        locationId: scenarioData.location || 0,
        timeOfDay: 'night',
        activeCharacters: [],
        goalChanges: {}
      };
    }
  }

  async _enhanceStateDiffWithServiceData(stateDiff) {
    try {
      const currentMetadata = await extractCurrentStateMetadata(this.hangoutState);
      const enhancedState = { ...currentMetadata, ...stateDiff };

      const validation = validateStateDiff(enhancedState);
      if (!validation.isValid) {
        console.warn('State validation warnings:', validation.errors);
      }

      return enhancedState;

    } catch (error) {
      return stateDiff;
    }
  }

  async _applyRestoredState(restoredState, restorationData) {
    try {
      const results = {
        charactersApplied: false,
        locationApplied: false,
        timeApplied: false,
        messagesApplied: false,
        goalChangesApplied: false
      };

      if (restorationData.messages?.length > 0) {
        this.hangoutState.setMessages(restorationData.messages);
        const conversationId = restorationData.conversationId
          || this.hangoutState.getConversationId?.()
          || null;
        this.hangoutState._loadActiveVersionsForConversation?.(conversationId);
        results.messagesApplied = true;
      }

      if (restoredState.locId !== undefined && restoredState.locId !== null) {
        try {
          // Check if user has already set a valid location via settings modal
          // If so, don't overwrite it with the restored location from message history
          const { storageManager } = await import('../storage/index.js');
          const currentConfig = await storageManager.getItem('currentHangoutConfig') || {};
          const currentLocId = currentConfig.locId;
          
          // Valid user-set location: not placeholder values like '0', '1', 'ERROR_*', etc.
          const isValidUserSetLocation = currentLocId && 
            currentLocId !== '0' && 
            currentLocId !== '1' && 
            !String(currentLocId).startsWith('ERROR') &&
            String(currentLocId) !== String(restoredState.locId); // Different from restored
          
          if (isValidUserSetLocation) {
            console.log('ConversationRestorationService: Preserving user-set location:', currentLocId, 'instead of restored:', restoredState.locId);
            results.locationApplied = false; // User's choice takes precedence
          } else {
            // No valid user override, apply the restored location
            await this.hangoutState.setSelectedBackground(restoredState.locId);
            results.locationApplied = true;
          }
        } catch (error) {
          console.warn('Location apply failed:', error);
        }
      }

      if (restoredState.timeOfDay) {
        try {
          await this.hangoutState.setTimeSlot(restoredState.timeOfDay, false);
          results.timeApplied = true;
        } catch (error) {
          console.warn('Time apply failed:', error);
        }
      }

      if (Array.isArray(restoredState.activeCharacters)) {
        try {
          await this._applyCharacterState(restoredState.activeCharacters);
          results.charactersApplied = true;
        } catch (error) {
          console.warn('Characters apply failed:', error);
        }
      }

      if (restoredState.goalChanges && Object.keys(restoredState.goalChanges).length > 0) {
        try {
          await this._applyGoalChanges(restoredState.goalChanges);
          results.goalChangesApplied = true;
        } catch (error) {
          console.warn('Goal changes apply failed:', error);
        }
      }

      const lastCharacterMessage = restorationData.messages
        ?.slice()
        .reverse()
        .find(msg => msg.role === 'assistant');

      eventBus.emit(HANGOUT_EVENTS.CONVERSATION_RESUMED, {
        lastMessage: lastCharacterMessage,
        totalMessages: restorationData.messages?.length || 0,
        shouldUpdateUI: true,
        conversationId: restorationData.conversationId,
        restoredState,
        results,
        source: 'ConversationRestorationService'
      });

      return results;

    } catch (error) {
      return { error: error.message };
    }
  }

  async _applyCharacterState(activeCharacters) {
    const hangoutConfig = await storageManager.getItem('currentHangoutConfig') || {};

    // Preserve existing character names if they exist and are better than what we're applying
    const existingCharacters = hangoutConfig.allCharacters || [];
    console.log('ConversationRestorationService._applyCharacterState: Existing characters:', existingCharacters);
    console.log('ConversationRestorationService._applyCharacterState: Incoming characters:', activeCharacters);

    // CRITICAL: If incoming activeCharacters is empty but we have existing characters, preserve them
    // This happens during page refresh when no character state is available
    if ((!activeCharacters || activeCharacters.length === 0) && existingCharacters.length > 0) {
      console.log('ConversationRestorationService._applyCharacterState: Incoming characters empty, preserving existing characters');
      // Don't overwrite - just return early to keep existing characters intact
      return;
    }

    const enhancedCharacters = activeCharacters.map(char => {
      const existingChar = existingCharacters.find(ec => String(ec.id) === String(char.id));

      // Preserve existing name if it's better than the new one, or use conversation character name
      let finalName = char.name;

      // First, try to use the character name from the conversation blob
      if (this._conversationCharacterName && String(char.id) === String(this._conversationCharacterName.split(' ').pop())) {
        // If conversation has character name and this is the matching character
        if (this._conversationCharacterName !== 'Character 4' && this._conversationCharacterName !== 'Character' && this._conversationCharacterName !== 'Unknown') {
          finalName = this._conversationCharacterName;
          console.log(`ConversationRestorationService._applyCharacterState: Using conversation character name for ${char.id}: "${finalName}"`);
        }
      } else if (this._conversationAllCharacters) {
        // Try to find character in allCharacters array
        const conversationChar = this._conversationAllCharacters.find(c => String(c.id) === String(char.id));
        if (conversationChar?.name && conversationChar.name !== 'Character' && conversationChar.name !== 'Unknown') {
          finalName = conversationChar.name;
          console.log(`ConversationRestorationService._applyCharacterState: Using conversation allCharacters name for ${char.id}: "${finalName}"`);
        }
      }

      // Fallback to existing character name if it's better
      if ((!finalName || finalName === 'Character' || finalName === 'Unknown' || finalName.startsWith('Character ')) &&
        existingChar?.name &&
        existingChar.name !== 'Character' &&
        existingChar.name !== 'Unknown' &&
        !existingChar.name.startsWith('Character ')) {
        finalName = existingChar.name;
        console.log(`ConversationRestorationService._applyCharacterState: Preserved existing name for ${char.id}: "${finalName}"`);
      } else {
        console.log(`ConversationRestorationService._applyCharacterState: Final name for ${char.id}: "${finalName}"`);
      }

      return {
        ...char,
        name: finalName || `Character ${char.id}`
      };
    });

    console.log('ConversationRestorationService._applyCharacterState: Final enhanced characters:', enhancedCharacters.map(c => ({ id: c.id, name: c.name })));

    // Fetch voice_id for each character and store in preferences
    // Need to map provider_voice_id to actual voice ID
    try {
      const { HangoutService } = await import('../../js/hangout/services/HangoutService.js');
      const { storage } = await import('../../js/utils/storage.js');
      const { getAllVoices } = await import('../../js/services/voiceService.js');
      const { normalizeGender } = await import('../../js/utils/genderUtils.js');
      const hangoutService = HangoutService.getInstance();
      const voiceIdPreferences = storage.get('characterVoiceIds') || {};

      for (const char of enhancedCharacters) {
        if (!voiceIdPreferences[char.id]) {
          try {
            const characterDetails = await hangoutService.getCharacterDetails(char.id);
            if (characterDetails?.voice_id) {
              const normalizedGender = normalizeGender(characterDetails);
              const voiceSubDir = normalizedGender === 'male' ? 'male_voices' : 'voices';
              const availableVoices = await getAllVoices(voiceSubDir);

              if (availableVoices && availableVoices.length > 0) {
                const jsonVid = characterDetails.voice_id;
                const jsonVidStr = String(jsonVid);
                const voiceToFind = availableVoices.find(v =>
                  v.provider_voice_id === jsonVid ||
                  String(v.provider_voice_id) === jsonVidStr
                );

                if (voiceToFind) {
                  voiceIdPreferences[char.id] = voiceToFind.id;
                  console.log(`ConversationRestorationService: Stored mapped voice_id ${voiceToFind.name} (${voiceToFind.id}) for character ${char.id}`);
                } else {
                  console.warn(`ConversationRestorationService: Could not find voice mapping for provider_voice_id ${characterDetails.voice_id}`);
                }
              }
            }
          } catch (error) {
            console.warn(`ConversationRestorationService: Failed to fetch voice_id for character ${char.id}:`, error);
          }
        }
      }

      storage.set('characterVoiceIds', voiceIdPreferences);
    } catch (error) {
      console.warn('ConversationRestorationService: Failed to fetch voice_ids:', error);
    }

    hangoutConfig.allCharacters = enhancedCharacters;
    await storageManager.setItem('currentHangoutConfig', hangoutConfig);

    // Also set characters in state for voice generation and other features
    await this.hangoutState.setCharacters(enhancedCharacters);

    const currentSession = this.hangoutState.getCurrentSession();
    if (currentSession) {
      currentSession.allCharacters = enhancedCharacters;
      currentSession.activeCharacterIds = enhancedCharacters.map(c => String(c.id));
      if (enhancedCharacters.length > 0) {
        // Update session to reflect the primary character (always first in allCharacters)
        currentSession.characterId = String(enhancedCharacters[0].id);
        currentSession.outfitId = String(enhancedCharacters[0].outfitId);
      }
    }
  }

  async _applyGoalChanges(goalChanges) {
    for (const [characterId, goal] of Object.entries(goalChanges)) {
      if (goal !== null) {
        await this.hangoutState.updateCurrentGoal(goal, characterId);
      }
    }
  }

}

export const conversationRestorationService = new ConversationRestorationService();

